/**
 * Company: SPHMedia
 * Description: [Brief description of the component]
 */

import type { BFFArticleCard } from "@src/app/types/bff";
import type { Section } from "@src/app/types/Data";
import clsx from "clsx";
import type { ReactElement } from "react";

import Link from "../Link";
import OptimisedImage from "../OptimisedImage/OptimisedImage";
import { SliceTag } from "../SectionTag";
import ArticleTypeIcon from "./ArticleTypeIcon";

type VerticalCardProps = {
  // Define the type for the props required by the component (if any)
  article: BFFArticleCard;
  theme?: "light" | "dark";
  showImage?: boolean;
  showSubHeader?: boolean;
  className?: string;
  testId?: number | string;
  isVideoPlaying?: boolean;
  isVideoPaused?: boolean;
  descStyle?: string;
  infoStyle?: string;
};

/**
 * VerticalCard
 * Description: [Description of what the component does]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function VerticalCard({
  article,
  theme = "light",
  showImage = true,
  showSubHeader = true,
  className,
  testId,
  isVideoPlaying,
  isVideoPaused,
  descStyle,
  infoStyle,
}: VerticalCardProps): ReactElement {
  return (
    <div
      className={clsx(" px-3 py-4", className)}
      data-testid={`vertical-card-${testId}`}
    >
      <div
        className={clsx("group relative lg:max-w-[300px]", {
          "bg-inverse": theme === "dark",
        })}
      >
        <Link to={article.urlPath}>
          {showImage && (
            <OptimisedImage
              rootClassName="aspect-[3/2] lg:group-hover:scale-105"
              src={article.cover}
              suffix={
                <ArticleTypeIcon
                  itemTypeProps={article.mediaType}
                  isVideoPlaying={isVideoPlaying}
                  isVideoPaused={isVideoPaused}
                />
              }
            />
          )}
          <div
            className={clsx(
              "flex flex-col gap-[8px] p-[16px]",
              theme === "dark" ? "bg-inverse pb-0" : "bg-default",
              descStyle,
              theme === "light" && "shadow-[3px_3px_5px_0_rgba(0,0,0,0.07)]",
            )}
          >
            <SliceTag section={article.section as Exclude<Section, "home">} />

            <div
              // 108 = 3 × text-header-3 height
              className={clsx(
                "flex flex-col gap-[8px] lg:min-h-[calc(108px+8px+28px)]",
                infoStyle,
              )}
            >
              <h3
                className={clsx(
                  "line-clamp-3 break-all text-header-3 text-primary lg:group-hover:underline",
                  { "text-primary-on-dark": theme === "dark" },
                )}
              >
                {article.title}
              </h3>
              {showSubHeader && (
                <h4 className="line-clamp-1 min-h-[22px] break-all text-subheader text-tertiary">
                  {article.description}
                </h4>
              )}
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
